import {
  AvatarSlot,
  AvatarSlots,
  AvatarState,
  SlotValue,
} from "@/shared/types/avatar";
import { AvatarAnimation } from "@/shared/types/animation";
import { SpineGameObject } from "@esotericsoftware/spine-phaser-v4";
import { AVATAR_SLOTS } from "../avatar/slot";
import { createDefaultAvatarState } from "@/shared/utils/avatar";

type CreateSpineObjectFn = (
  x: number,
  y: number,
  dataKey: string,
  atlasKey: string,
) => SpineGameObject;

export class AvatarSystem {
  private hero: SpineGameObject | null = null;
  private state: AvatarState = createDefaultAvatarState();

  constructor(
    private scene: Phaser.Scene,
    private createSpineObject: CreateSpineObjectFn,
  ) {}

  spawn(x: number, y: number): SpineGameObject {
    this.hero = this.createSpineObject(x, y, "man", "manAtlas");
    this.hero.setDepth(10);
    this.hero.setScale(0.8);
    this.hero.animationState.data.defaultMix = 0.15;
    this.hero.animationState.setAnimation(0, "Idle", true);
    this.hero.skeleton.scaleX = Math.abs(this.hero.skeleton.scaleX);

    this.applyState(this.state);

    return this.hero;
  }

  getState(): AvatarState {
    return this.state;
  }

  applyState(nextState: AvatarState): void {
    this.state = nextState;

    for (const key of Object.keys(nextState.slots) as AvatarSlot[]) {
      this.applyMappedSlot(key, nextState.slots[key]);
    }
  }

  setSlot(slot: AvatarSlot, attachment: SlotValue): void {
    this.state = {
      ...this.state,
      slots: {
        ...this.state.slots,
        [slot]: attachment,
      },
    };

    this.applyMappedSlot(slot, attachment);
  }

  setSlots(partial: Partial<AvatarSlots>): void {
    this.state = {
      ...this.state,
      slots: {
        ...this.state.slots,
        ...partial,
      },
    };

    for (const key of Object.keys(partial) as AvatarSlot[]) {
      this.applyMappedSlot(key, this.state.slots[key]);
    }
  }

  clearSlot(slot: AvatarSlot): void {
    this.setSlot(slot, null);
  }

  clearSlots(slots: AvatarSlot[]): void {
    const patch: Partial<AvatarSlots> = {};

    for (const slot of slots) {
      patch[slot] = null;
    }

    this.setSlots(patch);
  }

  clearAllSlots(): void {
    const empty = createDefaultAvatarState();
    this.applyState(empty);
  }

  playTapReaction(): void {
    if (!this.hero) return;

    this.scene.tweens.killTweensOf(this.hero);

    this.scene.tweens.add({
      targets: this.hero,
      scaleX: 0.82,
      scaleY: 0.78,
      duration: 60,
      yoyo: true,
      ease: "Quad.Out",
      onComplete: () => {
        if (!this.hero) return;
        this.hero.setScale(0.8);
      },
    });
  }

  playAnimation(animation: AvatarAnimation): void {
    if (!this.hero) return;

    this.hero.animationState.setAnimation(0, animation, false);
    this.hero.animationState.addAnimation(0, "Idle", true, 0);
  }

  showTemporarySlot(
    slot: AvatarSlot,
    attachment: string,
    duration = 500,
  ): void {
    const previous = this.state.slots[slot];

    this.setSlot(slot, attachment);

    this.scene.time.delayedCall(duration, () => {
      this.setSlot(slot, previous);
    });
  }

  private applyMappedSlot(slot: AvatarSlot, attachment: SlotValue): void {
    if (!this.hero) return;

    const spineSlotName = AVATAR_SLOTS[slot];
    this.hero.skeleton.setAttachment(spineSlotName, attachment ?? "");
  }
}
